import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUpload } from '../contexts/UploadContext';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  useTheme,
} from '@mui/material';
import {
  CloudUpload,
  Description,
  CheckCircle,
  ErrorOutline,
  Close,
  ArrowForward,
  UploadFile,
} from '@mui/icons-material';
// BRSyncButton removed — sync is now in the persistent AppHeader (App.js)

const VENDORS = [
  { value: 'american-eagle',      label: 'American Eagle',        color: '#c41230', abbr: 'AE'  },
  { value: 'american-eagle-mens', label: 'American Eagle Men',    color: '#a01025', abbr: 'AEM' },
  { value: 'aerie',               label: 'Aerie',                 color: '#e85d8a', abbr: 'AR'  },
  { value: 'chico',               label: "Chico's",               color: '#7c3aed', abbr: 'CH'  },
  { value: 'freepeople',          label: 'Free People',           color: '#d97706', abbr: 'FP'  },
  { value: 'lane-bryant',         label: 'Lane Bryant',           color: '#1d4ed8', abbr: 'LB'  },
  { value: 'mast',                label: "Victoria's Secret",     color: '#059669', abbr: 'VS'  },
  { value: 'pink',                label: 'Pink',                  color: '#db2777', abbr: 'PK'  },
  { value: 'loft',                label: 'Ann Taylor Loft',       color: '#b45309', abbr: 'LO'  },
  { value: 'loft-outlet',         label: 'Ann Taylor Loft Outlet',color: '#92400e', abbr: 'LFO' },
  { value: 'ann-taylor',          label: 'Ann Taylor',            color: '#78350f', abbr: 'AT'  },
  { value: 'ann-taylor-factory',  label: 'Ann Taylor Factory',    color: '#6b3a0f', abbr: 'ATF' },
  { value: 'talbots',             label: 'Talbots',               color: '#be123c', abbr: 'TA'  },
];

const VendorCard = ({ vendor, selected, onClick }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      onClick={onClick}
      sx={{
        p: '10px 12px',
        borderRadius: '10px',
        border: '1.5px solid',
        borderColor: selected
          ? vendor.color
          : isDark ? 'rgba(148,163,184,0.1)' : 'rgba(15,23,42,0.1)',
        backgroundColor: selected
          ? `${vendor.color}15`
          : isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        '&:hover': {
          borderColor: vendor.color,
          backgroundColor: `${vendor.color}10`,
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: '7px',
          backgroundColor: selected ? vendor.color : `${vendor.color}22`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'all 0.15s ease',
        }}
      >
        <Typography
          sx={{
            fontSize: '0.62rem',
            fontWeight: 800,
            color: selected ? '#fff' : vendor.color,
            fontFamily: '"DM Sans", sans-serif',
            letterSpacing: '0.02em',
          }}
        >
          {vendor.abbr}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: '0.8rem',
          fontWeight: selected ? 600 : 500,
          color: selected
            ? isDark ? '#e2e8f0' : '#0f172a'
            : isDark ? 'rgba(226,232,240,0.7)' : 'rgba(15,23,42,0.65)',
          transition: 'color 0.15s ease',
          lineHeight: 1.2,
        }}
      >
        {vendor.label}
      </Typography>
      {selected && (
        <CheckCircle
          sx={{
            fontSize: 14,
            color: vendor.color,
            ml: 'auto',
            flexShrink: 0,
          }}
        />
      )}
    </Box>
  );
};

const FileItem = ({ file, onRemove }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const sizeMB = (file.size / 1024 / 1024).toFixed(2);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: '8px',
        backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        border: `1px solid ${isDark ? 'rgba(148,163,184,0.08)' : 'rgba(15,23,42,0.07)'}`,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '7px',
          backgroundColor: 'rgba(99,102,241,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Description sx={{ fontSize: 16, color: '#818cf8' }} />
      </Box>
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Typography
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: isDark ? '#e2e8f0' : '#0f172a',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {file.name}
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }}>
          {sizeMB} MB
        </Typography>
      </Box>
      <Box
        onClick={onRemove}
        sx={{
          width: 24,
          height: 24,
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: isDark ? 'rgba(148,163,184,0.5)' : 'rgba(100,116,139,0.5)',
          '&:hover': {
            backgroundColor: 'rgba(239,68,68,0.1)',
            color: '#ef4444',
          },
          transition: 'all 0.15s ease',
          flexShrink: 0,
        }}
      >
        <Close sx={{ fontSize: 14 }} />
      </Box>
    </Box>
  );
};

const FileUpload = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');

  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { startJob } = useUpload();
  // Navigation on completion is handled globally by AppLayout (completionNotice)
  // which navigates directly to batch audit — no local redirect needed here.

  const onDrop = useCallback((acceptedFiles) => {
    const pdfFiles = acceptedFiles.filter((f) => f.type === 'application/pdf');
    if (pdfFiles.length !== acceptedFiles.length) {
      setError('Only PDF files are allowed');
      return;
    }
    setFiles(pdfFiles);
    setError('');
  }, []);

  // `open` is react-dropzone's built-in trigger for the file dialog.
  // Using it (instead of a manual ref click) lets dropzone manage its own
  // internal input ref — crucially, it can then reset the input's value after
  // each selection, so re-selecting the same file always fires onChange.
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
  });

  const handleUpload = async () => {
    if (files.length === 0) { setError('Please select at least one PDF file'); return; }
    if (!selectedVendor) { setError('Please select a customer'); return; }
    setUploading(true);
    setError('');
    setSuccess('');
    // Normalise vendor value to the underscore form the API expects
    const vendorKey = selectedVendor.replace(/-/g, '_');
    try {
      await startJob(vendorKey, files);
      setSuccess(`Processing started — you'll be taken to batch audit automatically when done.`);
      setFiles([]);
      setSelectedVendor('');
    } catch (err) {
      console.error('[FileUpload] startJob failed', err);
      setError(err.response?.data?.detail || 'Failed to start upload. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index) => setFiles(files.filter((_, i) => i !== index));

  const selectedVendorObj = VENDORS.find((v) => v.value === selectedVendor);
  const canUpload = files.length > 0 && selectedVendor && !uploading;

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      {/* Page header */}
      <Box sx={{ mb: { xs: 3, sm: 4 }, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.025em',
              mb: 0.75,
              fontSize: { xs: '1.5rem', sm: '1.875rem' },
              background: isDark
                ? 'linear-gradient(135deg, #e2e8f0 0%, #94a3b8 100%)'
                : 'linear-gradient(135deg, #0f172a 0%, #475569 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Upload Purchase Orders
          </Typography>
          <Typography sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
            Select a customer parser, drop your PDF documents, and let the system extract the data automatically.
          </Typography>
        </Box>

        {/* Sync BR is in the persistent AppHeader — nothing needed here */}
      </Box>

      {/* Step 1: Customer — full-width card grid, no scroll */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Box sx={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: '#fff', fontSize: '0.65rem', fontWeight: 700 }}>1</Typography>
          </Box>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: isDark ? '#94a3b8' : '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Select Customer
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 1 }}>
          {VENDORS.map((vendor) => (
            <VendorCard
              key={vendor.value}
              vendor={vendor}
              selected={selectedVendor === vendor.value}
              onClick={() => setSelectedVendor(selectedVendor === vendor.value ? '' : vendor.value)}
            />
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: { xs: 2, sm: 3 } }}>
        {/* Left: Drop zone */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Drop zone */}
            <Box
              {...getRootProps()}
              sx={{
                position: 'relative',
                borderRadius: '12px',
                border: '2px dashed',
                borderColor: isDragActive
                  ? '#6366f1'
                  : files.length > 0
                  ? '#10b981'
                  : isDark ? 'rgba(148,163,184,0.15)' : 'rgba(15,23,42,0.12)',
                backgroundColor: isDragActive
                  ? 'rgba(99,102,241,0.06)'
                  : files.length > 0
                  ? 'rgba(16,185,129,0.04)'
                  : isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                p: 4,
                textAlign: 'center',
                '&:hover': {
                  borderColor: '#6366f1',
                  backgroundColor: 'rgba(99,102,241,0.04)',
                },
              }}
            >
              <input {...getInputProps()} />
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '14px',
                  mx: 'auto',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isDragActive
                    ? 'linear-gradient(135deg, #6366f1, #818cf8)'
                    : files.length > 0
                    ? 'linear-gradient(135deg, #10b981, #34d399)'
                    : isDark ? 'rgba(99,102,241,0.15)' : 'rgba(79,70,229,0.08)',
                  transition: 'all 0.2s ease',
                }}
              >
                {files.length > 0 ? (
                  <CheckCircle sx={{ fontSize: 26, color: isDragActive ? '#fff' : '#10b981' }} />
                ) : (
                  <UploadFile sx={{ fontSize: 26, color: isDragActive ? '#fff' : '#6366f1' }} />
                )}
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: isDark ? '#e2e8f0' : '#0f172a', mb: 0.5 }}>
                {isDragActive ? 'Drop files here' : files.length > 0 ? 'Files ready' : 'Drop PDF files here'}
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: isDark ? '#94a3b8' : '#64748b', mb: 2 }}>
                {files.length > 0 ? `${files.length} file(s) selected` : 'or click to browse your computer'}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloudUpload sx={{ fontSize: 15 }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  open();
                }}
                sx={{
                  borderColor: isDark ? 'rgba(148,163,184,0.2)' : 'rgba(15,23,42,0.15)',
                  color: isDark ? '#94a3b8' : '#64748b',
                  fontSize: '0.8rem',
                  px: 2,
                  py: 0.75,
                  '&:hover': {
                    borderColor: '#6366f1',
                    color: '#6366f1',
                    backgroundColor: 'rgba(99,102,241,0.04)',
                  },
                }}
              >
                Browse Files
              </Button>
            </Box>
        </Box>

        {/* Right: File list + alerts + actions */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'flex-start' }}>
          {/* Step 2 label */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: files.length > 0 ? '#10b981' : isDark ? 'rgba(148,163,184,0.3)' : 'rgba(100,116,139,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.2s' }}>
              <Typography sx={{ color: '#fff', fontSize: '0.65rem', fontWeight: 700 }}>2</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: isDark ? '#94a3b8' : '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Add Files
            </Typography>
            {files.length > 0 && (
              <Chip label={`${files.length} file${files.length > 1 ? 's' : ''}`} size="small" sx={{ backgroundColor: 'rgba(16,185,129,0.15)', color: '#10b981', fontWeight: 600, fontSize: '0.7rem', height: 20, border: '1px solid rgba(16,185,129,0.3)' }} />
            )}
          </Box>

          {/* File list */}
          {files.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 200, overflowY: 'auto', pr: 0.5,
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: isDark ? 'rgba(148,163,184,0.2)' : 'rgba(15,23,42,0.12)', borderRadius: 2 },
              }}>
                {files.map((file, index) => (
                  <FileItem key={index} file={file} onRemove={() => removeFile(index)} />
                ))}
              </Box>
            </Box>
          )}

          {/* Alerts */}
          {error && (
            <Alert severity="error" onClose={() => setError('')} icon={<ErrorOutline fontSize="small" />} sx={{ '& .MuiAlert-message': { fontSize: '0.8125rem' } }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" onClose={() => setSuccess('')} sx={{ '& .MuiAlert-message': { fontSize: '0.8125rem' } }}>
              {success}
            </Alert>
          )}

          {/* Action buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 'auto' }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleUpload}
              disabled={!canUpload}
              startIcon={<UploadFile />}
              sx={{
                py: 1.5,
                fontSize: '0.9375rem',
                color: '#fff',
                background: canUpload
                  ? selectedVendorObj
                    ? `linear-gradient(135deg, ${selectedVendorObj.color}dd, ${selectedVendorObj.color})`
                    : '#4a4f5a'
                  : undefined,
                '&:hover': {
                  background: canUpload && selectedVendorObj
                    ? `linear-gradient(135deg, ${selectedVendorObj.color}, ${selectedVendorObj.color}cc)`
                    : undefined,
                },
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' },
              }}
            >
              {uploading ? 'Starting…' : 'Process PDFs'}
            </Button>

            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={() => navigate('/dashboard')}
              endIcon={<ArrowForward />}
              sx={{
                py: 1.5,
                borderColor: isDark ? 'rgba(148,163,184,0.2)' : 'rgba(15,23,42,0.12)',
                color: isDark ? '#94a3b8' : '#64748b',
                '&:hover': { borderColor: '#6366f1', color: '#6366f1', backgroundColor: 'rgba(99,102,241,0.04)' },
              }}
            >
              View Dashboard
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FileUpload;
